import { Box, Paper, Typography, alpha, useTheme, Chip, Stack, IconButton, Tooltip, Avatar, Button, Grid } from "@mui/material";
import { useState } from "react";
import { LuMessageSquare, LuClock, LuZap, LuUser, LuFilter, LuExternalLink } from "react-icons/lu";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const SupportManagement = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Mock Tickets for Master Admin View
    const [tickets] = useState([
        {
            id: "T-88291",
            subject: "Cannot export project data to PDF",
            user: "John Doe",
            email: "john@example.com",
            category: "Technical",
            priority: "High",
            status: "Open",
            createdAt: dayjs().subtract(2, 'hours').toISOString(),
            message: "I am getting a 500 error whenever I try to click the export button on the Projects dashboard."
        },
        {
            id: "T-88285",
            subject: "Inquiry about new API limits",
            user: "Sarah Smith",
            email: "sarah.s@enterprise.com",
            category: "Billing",
            priority: "Medium",
            status: "In Progress",
            createdAt: dayjs().subtract(1, 'day').toISOString(),
            message: "We are planning to increase our usage next month. Can we get a quote for higher API rate limits?"
        },
        {
            id: "T-88270",
            subject: "Feature request: Dark mode toggle",
            user: "Mike Ross",
            email: "m.ross@legal.com",
            category: "Feature",
            priority: "Low",
            status: "Resolved",
            createdAt: dayjs().subtract(3, 'days').toISOString(),
            message: "It would be great if we could have a dark mode toggle specifically for the circularity charts."
        }
    ]);

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return '#ef4444';
            case 'Medium': return '#f59e0b';
            case 'Low': return '#10b981';
            default: return theme.palette.primary.main;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Open': return '#ef4444';
            case 'In Progress': return '#3b82f6';
            case 'Resolved': return '#10b981';
            default: return 'gray';
        }
    };

    return (
        <Stack spacing={4} sx={{ bgcolor: 'transparent', border: 'none', boxShadow: 'none' }}>
            {/* Header Info */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 1000, color: 'text.primary', letterSpacing: '-1.5px', mb: 1 }}>
                        Support Queue
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Management console for all user support requests and system tickets.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button startIcon={<LuFilter />} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                        Filter
                    </Button>
                    <Button startIcon={<LuExternalLink />} variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                        Export Logs
                    </Button>
                </Stack>
            </Box>

            {/* Stats Quick View */}
            <Grid container spacing={3} sx={{ mb: 5 }}>
                {[
                    { label: 'Pending Tickets', count: '12', color: '#ef4444' },
                    { label: 'In Progress', count: '5', color: '#3b82f6' },
                    { label: 'Average Response', count: '1.4h', color: '#10b981' }
                ].map((stat, i) => (
                    <Grid item xs={12} md={4} key={i}>
                        <Box elevation={0} sx={{
                            p: 3,
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.05),
                            bgcolor: 'transparent'
                        }}>
                            <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary' }}>{stat.label}</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 1000, color: stat.color }}>{stat.count}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Tickets List */}
            <Stack spacing={2}>
                {tickets.map((ticket) => (
                    <Box
                        key={ticket.id}
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
                            bgcolor: 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: alpha(theme.palette.primary.main, 0.4),
                                boxShadow: isDark ? 'none' : '0 8px 25px -10px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: alpha(theme.palette.primary.main, 0.1), px: 1, py: 0.2, borderRadius: 1 }}>
                                            {ticket.id}
                                        </Typography>
                                        <Chip
                                            label={ticket.category}
                                            size="small"
                                            sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20, bgcolor: alpha(theme.palette.text.primary, 0.05) }}
                                        />
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getPriorityColor(ticket.priority) }} />
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: getPriorityColor(ticket.priority) }}>
                                            {ticket.priority} Priority
                                        </Typography>
                                    </Stack>
                                    <Typography variant="h6" sx={{ fontWeight: 1000, color: 'text.primary' }}>{ticket.subject}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {ticket.message}
                                    </Typography>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.main }}>
                                        <LuUser size={18} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{ticket.user}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{ticket.email}</Typography>
                                    </Box>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Chip
                                            label={ticket.status}
                                            sx={{
                                                fontWeight: 900,
                                                bgcolor: alpha(getStatusColor(ticket.status), 0.1),
                                                color: getStatusColor(ticket.status),
                                                borderRadius: 2
                                            }}
                                        />
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontWeight: 700 }}>
                                            <LuClock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                            {dayjs(ticket.createdAt).fromNow()}
                                        </Typography>
                                    </Box>
                                    <IconButton sx={{ bgcolor: alpha(theme.palette.text.primary, 0.05) }}>
                                        <LuZap size={20} />
                                    </IconButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Stack>
        </Stack>
    );
};

export default SupportManagement;
